import { type NumberPrecision } from '@blaze-shared/api-contracts-domain';
import { type TableColumnFormat } from '@blaze-shared/elements-domain';
import { getPreciseCurrencyFormatter, getPreciseNumberFormatter } from '@blaze-shared/shared-util';

import { type SliderFormat } from './slider.models';

const MAX_HANDLE_SIZE_PX = 48;

const SLIDER_FORMATS = new Set<SliderFormat>(['number', 'percentage', 'currency', 'integer']);

export function toSliderFormat(
  format: TableColumnFormat | SliderFormat | undefined,
): SliderFormat | undefined {
  return SLIDER_FORMATS.has(format as SliderFormat) ? (format as SliderFormat) : undefined;
}

export type FormatSliderValueOptions = {
  format: SliderFormat;
  precision?: NumberPrecision;
  currency?: string;
};

function stripFloatNoise(value: number): number {
  return Number(value.toFixed(10));
}

export function dataValueToInternal(value: number, format: SliderFormat): number {
  return format === 'percentage' ? stripFloatNoise(value * 100) : value;
}

export function internalValueToData(value: number, format: SliderFormat): number {
  return format === 'percentage' ? stripFloatNoise(value / 100) : value;
}

export function clampHandleSize(sizePx: number): number {
  return Math.min(Math.max(sizePx, 8), MAX_HANDLE_SIZE_PX);
}

export function clampTrackWeight(weightPx: number, handleSizePx: number): number {
  const maxWeight = handleSizePx / 2;
  return Math.min(Math.max(weightPx, 1), maxWeight);
}

export function resolveStep(
  configuredStep: number | string | undefined,
  format?: SliderFormat,
): number {
  let step = configuredStep == null || configuredStep === '' ? 1 : Number(configuredStep);
  if (!Number.isFinite(step) || step <= 0) {
    step = 1;
  }

  if (format === 'integer') {
    return Math.max(1, step);
  }

  return step;
}

export function resolveSliderBounds(
  configuredMin: number,
  configuredMax: number,
  value?: number | null,
): { min: number; max: number } {
  const min = Math.min(configuredMin, configuredMax);
  const max = Math.max(configuredMin, configuredMax);

  if (value == null || Number.isNaN(value)) {
    return { min, max };
  }

  return { min: Math.min(min, value), max: Math.max(max, value) };
}

export function getSliderTickValues(min: number, max: number, step: number): number[] {
  if (max < min || step <= 0 || !Number.isFinite(min) || !Number.isFinite(max)) {
    return [];
  }

  const ticks: number[] = [];
  const precision = step < 1 ? (String(step).split('.')[1]?.length ?? 0) : 0;

  for (let value = min; value <= max + 1e-9; value += step) {
    ticks.push(Number(value.toFixed(precision)));
  }

  return ticks;
}

export function snapToStep(value: number, min: number, max: number, step: number): number {
  if (step <= 0) {
    return Math.min(max, Math.max(min, value));
  }
  const steps = Math.round((value - min) / step);
  const snapped = min + steps * step;

  return Number(Math.min(max, Math.max(min, snapped)));
}

export function formatSliderValue(value: number, options: FormatSliderValueOptions): string {
  const { format, precision, currency = 'USD' } = options;

  if (format === 'integer') {
    return getPreciseNumberFormatter(0).format(Math.round(value));
  }

  if (format === 'percentage') {
    return `${getPreciseNumberFormatter(precision ?? null).format(value)}%`;
  }

  if (format === 'currency') {
    return getPreciseCurrencyFormatter(currency, precision ?? null).format(value);
  }

  return getPreciseNumberFormatter(precision ?? null).format(value);
}
